import React, { useEffect, useState } from "react";
import { FormControl, FormLabel, Select, Spinner } from "@chakra-ui/react";
import { useAuth } from "./AuthContext";
import { getUserOrganisationIds } from "./utils/ownership";

const fetchOrganisationName = async (orgId) => {
  try {
    const res = await fetch(`https://directory.poc.mrc.ai/api/parties/${orgId}`);
    if (!res.ok) return orgId;
    const party = await res.json();
    return party?.name || party?.legalName || orgId;
  } catch {
    return orgId;
  }
};

/**
 * Picks one of the organisations the signed-in user is a member of (from the
 * roles in AuthContext). A current value the user is not a member of (e.g. a
 * record a platform admin is viewing) is still shown, so it isn't lost.
 */
const OrganisationSelect = ({
  label = "Organisation",
  selectedOrganisationId,
  onChange,
  size = "md",
  height = undefined,
  isDisabled = false,
  isRequired = false,
}) => {
  const { user } = useAuth();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);

  const memberOrgIds = getUserOrganisationIds(user);
  const memberOrgKey = memberOrgIds.join(",");

  useEffect(() => {
    const loadNames = async () => {
      setLoading(true);
      const ids = [...memberOrgIds];
      if (selectedOrganisationId && !ids.includes(selectedOrganisationId)) {
        ids.push(selectedOrganisationId);
      }
      const named = await Promise.all(
        ids.map(async (id) => ({
          id,
          name: await fetchOrganisationName(id),
          isMember: memberOrgIds.includes(id),
        }))
      );
      setOrganisations(named.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    };

    loadNames();
    // memberOrgKey stands in for memberOrgIds, which is a new array each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOrgKey, selectedOrganisationId]);

  return (
    <FormControl isRequired={isRequired}>
      {label && <FormLabel fontSize="xs">{label}</FormLabel>}
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <Select
          placeholder={
            organisations.length ? "Select organisation..." : "No organisations available"
          }
          value={selectedOrganisationId || ""}
          onChange={(e) => onChange(e.target.value || null)}
          size={size}
          height={height}
          isDisabled={isDisabled}>
          {organisations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.isMember ? org.name : `${org.name} (not a member)`}
            </option>
          ))}
        </Select>
      )}
    </FormControl>
  );
};

export default OrganisationSelect;
